import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const SERVICE_VARIANT_ID = '55195758330193';
const JACKET_HANDLE = 'arctavo-reactive-jacket';
const root = window.Shopify?.routes?.root ?? '/';
const cartUrl = `${root}cart.js`;
let requestInProgress = false;
let reconcileTimer = 0;

const getCart = async () => {
  const response = await fetch(cartUrl, { headers: { Accept: 'application/json' }, cache: 'no-store' });
  if (!response.ok) throw new Error(`Could not read Shopify cart (${response.status}).`);
  return response.json();
};

const serviceLines = (cart) => (cart.items ?? []).filter((item) => String(item.variant_id) === SERVICE_VARIANT_ID);
const jacketLines = (cart) => (cart.items ?? []).filter((item) => item.product_type !== 'service' && item.handle !== 'faster-shipping-priority-processing' && item.product_handle !== 'faster-shipping-priority-processing' && item.product_title !== 'Faster Shipping & Priority Processing');

const syncToggles = (cart) => {
  const services = serviceLines(cart);
  const selected = services.length > 0;
  document.querySelectorAll('[data-arctavo-cart-priority]').forEach((row) => {
    row.dataset.inCart = String(selected);
    row.dataset.lineKey = services[0]?.key ?? '';
    const toggle = row.querySelector('[data-arctavo-cart-priority-toggle]');
    if (!toggle) return;
    toggle.setAttribute('aria-checked', String(selected));
    toggle.toggleAttribute('data-selected', selected);
    toggle.disabled = requestInProgress;
  });
};

const sectionIds = () => [...new Set([...document.querySelectorAll('cart-items-component')]
  .map((component) => component.dataset.sectionId)
  .filter(Boolean))];

const requestCartChange = async ({ action, lines, body, endpoint }) => {
  if (requestInProgress) return;
  requestInProgress = true;
  document.querySelectorAll('[data-arctavo-cart-priority-toggle]').forEach((toggle) => {
    toggle.disabled = true;
    toggle.setAttribute('aria-busy', 'true');
  });

  const sectionList = sectionIds();
  const promise = CartLinesUpdateEvent.createPromise();
  promise.promise.catch(() => {});
  const event = new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: promise.promise });
  document.dispatchEvent(event);

  try {
    const requestBody = { ...body, sections: sectionList.join(','), sections_url: window.location.pathname };
    const response = await fetch(`${root}${endpoint}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(requestBody),
    });
    const result = await response.json();
    if (!response.ok || result.status || result.errors) {
      throw new Error(result.description ?? result.message ?? result.errors ?? `Shopify cart update failed (${response.status}).`);
    }
    const cart = await getCart();
    promise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        sections: result.sections,
        items: result.items,
        itemCount: cart.item_count,
        source: 'arctavo-cart-priority',
        didError: false,
      },
    });
    await promise.promise;
    return cart;
  } catch (error) {
    promise.reject(error);
    console.error('[ARCTAVO] Priority service cart update failed:', error);
    throw error;
  } finally {
    await new Promise((resolve) => window.setTimeout(resolve, 220));
    requestInProgress = false;
    document.querySelectorAll('[data-arctavo-cart-priority-toggle]').forEach((toggle) => {
      toggle.removeAttribute('aria-busy');
      toggle.disabled = false;
    });
    try {
      syncToggles(await getCart());
    } catch (error) {
      console.error('[ARCTAVO] Could not restore priority switch from Shopify cart:', error);
    }
  }
};

const changeServiceLine = async (cart, quantity) => {
  const services = serviceLines(cart);
  if (!services.length) {
    if (quantity !== 1) return cart;
    const lines = [{ merchandiseId: SERVICE_VARIANT_ID, quantity: 1 }];
    return requestCartChange({
      action: 'add',
      lines,
      endpoint: 'cart/add.js',
      body: { items: [{ id: Number(SERVICE_VARIANT_ID), quantity: 1 }] },
    });
  }

  const [first, ...duplicates] = services;
  const changes = [];
  if (first.quantity !== quantity) changes.push({ item: first, target: quantity });
  duplicates.forEach((item) => changes.push({ item, target: 0 }));
  let latestCart = cart;
  for (const { item, target } of changes) {
    latestCart = await requestCartChange({
      action: target === 0 ? 'remove' : 'update',
      lines: [{ id: item.key, quantity: target }],
      endpoint: 'cart/change.js',
      body: { id: item.key, quantity: target },
    });
  }
  return latestCart;
};

const reconcileCart = async () => {
  if (requestInProgress) return;
  try {
    let cart = await getCart();
    syncToggles(cart);
    const services = serviceLines(cart);
    const jackets = jacketLines(cart);
    if (services.length && jackets.length === 0) {
      cart = await changeServiceLine(cart, 0);
    } else if (services.length > 0 && (services.length > 1 || services[0].quantity !== 1)) {
      cart = await changeServiceLine(cart, 1);
    }
    syncToggles(cart);
  } catch (error) {
    console.error('[ARCTAVO] Could not reconcile priority service with Shopify cart:', error);
  }
};

document.addEventListener('click', async (event) => {
  const toggle = event.target instanceof Element ? event.target.closest('[data-arctavo-cart-priority-toggle]') : null;
  if (!toggle || requestInProgress) return;
  event.preventDefault();
  const row = toggle.closest('[data-arctavo-cart-priority]');
  if (!row) return;

  try {
    const cart = await getCart();
    const hasJackets = jacketLines(cart).length > 0;
    const selected = serviceLines(cart).length > 0;
    syncToggles(cart);
    if (!hasJackets && !selected) return;
    const updated = await changeServiceLine(cart, selected ? 0 : 1);
    syncToggles(updated);
  } catch (error) {
    try { syncToggles(await getCart()); } catch (refreshError) {
      console.error('[ARCTAVO] Could not restore priority switch state:', refreshError);
    }
  }
});

const scheduleReconcile = () => {
  if (requestInProgress) return;
  window.clearTimeout(reconcileTimer);
  reconcileTimer = window.setTimeout(reconcileCart, 180);
};

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (requestInProgress || !event.promise) return;
  event.promise.then(scheduleReconcile).catch(() => {});
});

document.addEventListener('theme-drawer:open', reconcileCart);
window.addEventListener('pageshow', reconcileCart);
document.addEventListener('DOMContentLoaded', reconcileCart, { once: true });

const drawer = document.querySelector('#cart-drawer');
if (drawer) {
  const observer = new MutationObserver(scheduleReconcile);
  observer.observe(drawer, { childList: true, subtree: true });
}
